<template>
  <component
    v-if="data && data.length"
    :is="config.isList ? 'fieldset' : 'div'"
    class="field-renderer"
    :class="{
      ['field-renderer--' + fieldName]: true,
      'field-renderer--collapse': collapsible,
      'field-renderer--expanded': expanded,
      'field-renderer--list': config.isList,
      'field-renderer--disabled': isDisabled,
    }"
    :style="style"
    v-auto-animate="{ disabled: !collapsible }"
  >
    <div class="field-renderer__disabled-overlay" v-if="isDisabled">
      <div class="field-renderer__disabled-overlay-content">
        <Icon :icon="['fad', 'lock']" size="2x" />
        <span class="field-renderer__disabled-overlay-info">{{ $t('lockedInfo') }}</span>
      </div>
    </div>
    <button
      v-if="collapsible"
      class="field-renderer__collapsible-header"
      @click="expand"
      type="button"
    >
      <Icon :icon="['fal', 'angle-down']" size="lg" :rotation="!expanded ? null : '180'" />
      <legend class="field-renderer__collapsible-labels">
        <div class="field-renderer__collapsible-labels-label">{{ schema.label }}</div>
        <div
          v-if="config.binding && config.binding.sublabel"
          class="field-renderer__collapsible-labels-sublabel"
        >
          {{ config.binding.sublabel }}
        </div>
      </legend>
      <span style="margin-left: auto">
        <template v-if="config.isList">
          {{ data.length }} {{ $tc('field', data.length).toLowerCase() }}
        </template>

        <Settings
          v-if="config.settings && !config.isList"
          :schema="schema"
          :config="config.settings"
          @input="$ev => set($ev, parentIndex)"
          v-bind="getBinding(parentIndex)"
          v-tooltip.bottom="getDisabledFieldsReason()"
        ></Settings>
      </span>
    </button>

    <template v-if="!collapsible || (collapsible && expanded)">
      <div v-for="(item, index) in data" :key="getId(index)" class="field-renderer__item">
        <div v-if="config.isList" class="field-renderer__actions">
          <ButtonComponent
            v-if="config.removable"
            v-tooltip="$t('remove')"
            :icon="['fal', 'trash']"
            :disabled="Boolean(getDisabledFieldsReason(index, true))"
            class="field-renderer__action-remove"
            type="button"
            @click="remove(index)"
          />
          <template v-if="config.isList && data.length > 1">
            <ButtonComponent
              :icon="['fal', 'arrow-up']"
              v-tooltip="$t('moveUp')"
              class="field-renderer__action-up"
              type="button"
              :disabled="
                index === 0 ||
                Boolean(getDisabledFieldsReason(index, true)) ||
                Boolean(getDisabledFieldsReason(index - 1, true))
              "
              @click="move(index, -1)"
            />
            <ButtonComponent
              :icon="['fal', 'arrow-down']"
              v-tooltip="$t('moveDown')"
              class="field-renderer__action-down"
              type="button"
              :disabled="index === data.length - 1 || Boolean(getDisabledFieldsReason(index, true))"
              @click="move(index, 1)"
            />
          </template>

          <Settings
            v-if="config.settings && config.isList"
            v-bind="getBinding(index)"
            :parent-index="index"
            class="field-renderer__action-settings"
            v-on="{
              ...$listeners,
              input: $ev => set($ev, index),
            }"
          ></Settings>
        </div>

        <component
          :is="config.component"
          class="custom-field"
          :class="['custom-field--' + fieldName]"
          v-bind="getBinding(index)"
          v-tooltip.bottom="getDisabledFieldsReason()"
          v-on="{
            ...$listeners,
            input: $ev => set($ev, index),
          }"
        >
        </component>
      </div>

      <ButtonComponent
        v-if="config.isList && config.addable"
        :disabled="Boolean(getDisabledFieldsReason(undefined, true))"
        v-tooltip.bottom="getDisabledFieldsReason(undefined, true)"
        :label="$t('createNewField')"
        :icon="['fas', 'plus-circle']"
        class="field-renderer__action-add"
        type="button"
        @click="add()"
      />
    </template>
  </component>
</template>

<script>
import { Clone, ExtractString, BracketsRegex, getDynamicComponentTemplate } from './utils'
import { path } from 'ramda'
import Templates from './Templates'
import { ButtonComponent } from 'vue-elder-button'
import AutoAnimate from './directives/auto-animate.js'
import Settings from './Settings.vue'
import Icon from '@kvass/vue2-icon'

import VTooltip from 'v-tooltip'

export default {
  props: {
    value: undefined,
    config: Object,
    schema: Object,
    defaultExpanded: Array,
    parentIndex: Number,
  },
  data() {
    return {
      expanded: false,
      ids: [],
    }
  },
  directives: {
    AutoAnimate,
  },
  inject: ['injection'],
  computed: {
    isDisabled() {
      let disabled = path(['binding', 'disabled'], this.config)

      if (this.config.componentName === 'Webcomponent' && disabled) return true

      return this.config.isList
        ? this.expanded && disabled
        : this.fieldName === 'dynamic' && disabled
    },
    validator() {
      return this.injection.validator
    },
    validatorPrefix() {
      return this.injection.validatorPrefix
    },
    data() {
      if (this.config.isList) return this.value
      return [this.value]
    },
    style() {
      let style = {
        '--list-item-min-width':
          (typeof this.schema.component === 'string'
            ? ExtractString(this.schema.component, BracketsRegex, { group: 2 })
            : null) ||
          this.config.minWidth ||
          'initial',
      }

      let size = path(['options', 'size'], this.schema)
      if (size) style['gridColumnEnd'] = `span ${size}`

      return style
    },
    fieldName() {
      return this.config.componentName.toLowerCase()
    },
    isParent() {
      if (this.schema.parentKey && this.schema.parentKey !== this.schema.key) return false
      return true
    },

    collapsible() {
      return this.config.collapsible || this.config.isList
    },
  },
  methods: {
    getDisabledFieldsReason(index, isSingle) {
      const key = [
        this.injection.disabledFieldsPrefix,
        this.schema.parentKey,
        this.schema.parentIndex,
        this.schema.key,
      ]
        .filter(v => ![undefined, null].includes(v))
        .join('.')

      const disabledFields = this.injection.disabledFields || {}
      const target = Object.entries(disabledFields).find(([field, reason]) => {
        const hasIndex = index || index === 0

        const keyHasTarget =
          field === key ||
          (hasIndex ? field.startsWith(`${key}.${index}`) : `${key}.`.startsWith(field))

        const isRootComponent = isSingle || this.fieldName !== 'dynamic'

        return keyHasTarget && isRootComponent
      })

      if (!target) return
      return target[1]
    },
    getId(index) {
      if (!this.ids[index]) this.ids[index] = crypto.randomUUID()
      return this.ids[index]
    },
    getBinding(index) {
      let base = this.config.binding

      let key = [
        this.schema.parentKey,
        this.schema.parentIndex,
        this.schema.key,
        this.config.isList ? index : null,
      ]
        .filter(e => ![null, undefined].includes(e))
        .join('.')

      let config = {
        ...this.config,
      }

      if (this.config.isList) config.index = index

      let result = {
        config,
        options: this.schema.options,
        label: this.schema.label,
        value:
          path(['value'], base) ||
          (this.config.isList && this.value ? this.value[index] : this.value),
        schema: this.schema,
        ...base,
      }

      if (this.collapsible && !this.config.showLabelCount) {
        delete result.label
        delete result.sublabel
      }

      if (
        this.validator &&
        [this.validatorPrefix, key.replace(/(\.)(\d+)(\.?)/g, '$1*$3')].filter(Boolean).join('.') in
          this.validator.fields
      ) {
        let fullKey = [this.validatorPrefix, key].filter(Boolean).join('.')
        let hasError = fullKey in this.validator.errors.errors
        result['isValid'] = !hasError
        result['validationMessage'] = hasError
          ? this.validator.errors.errors[fullKey].join(' - ')
          : null
      }

      if (this.config.showLabelCount && this.data.length > 1) {
        result.label = `${this.$tc('field', 1)} ${index + 1}`
        delete result.sublabel
      }

      return result
    },

    expand() {
      this.expanded = !this.expanded

      //emit to update localstorage expanded lists
      if (this.collapsible) {
        this.$emit('update:expanded:lists', {
          method: this.expanded ? 'add' : 'remove',
          key: this.config.parentKey,
          placement: this.config.placement,
        })
      }
    },

    set(val, i) {
      if (this.config.isList) return this.value.splice(i, 1, val)
      this.$emit('input', val)
    },
    add(i) {
      this.$emit('input', [...(this.value || []), this.getTemplate()])
    },

    remove(i) {
      this.ids.splice(i, 1)

      if (this.config.isList) {
        if (this.config.removable) {
          return this.$emit(
            'input',
            this.value.length > 1
              ? this.value.filter((v, ii) => i !== ii)
              : this.value
                  .map(({ key, value }) => ({ [key]: null }))
                  .filter(i => {
                    if ('undefined' in i) {
                      delete i['undefined']
                    }
                    return i
                  }),
          )
        }
      } else {
        if (!this.config.removable) return
        return this.$emit('remove', this.schema)
      }
    },
    move(index, delta) {
      // both data and ids do the same re-ordering
      ;[this.ids, this.value].forEach(arr => {
        const item = arr.splice(index, 1)[0]
        arr.splice(index + delta, 0, item)
      })

      this.$emit('input', this.value)
    },
    getTemplate() {
      if (this.config.componentName === 'Dynamic') return getDynamicComponentTemplate(this.config)
      return Templates[this.config.componentName]
        ? Clone(Templates[this.config.componentName])
        : undefined
    },

    setDefaultExpand() {
      //set as default expand if defined in defaultExpanded prop
      if (
        (this.defaultExpanded || []).includes(
          `${this.config.placement}:${this.config.parentKey}`,
        ) ||
        this.config.expanded
      )
        this.expand()
    },
  },
  mounted() {
    if (this.config.isList && (!this.value || !this.value.length)) this.add()
    if (this.collapsible) this.setDefaultExpand()
  },
  components: {
    ButtonComponent,
    Settings,
    Icon,
  },
  directives: {
    VTooltip,
  },
}
</script>

<style lang="scss">
.field-renderer {
  position: relative;
  $padding: 1rem;

  border-radius: var(--border-radius, 4px);
  &--collapse {
    display: grid !important;
    grid-template-columns: repeat(auto-fit, minmax(var(--list-item-min-width), 1fr)) !important;

    grid-gap: 1rem;
    grid-column-end: span 2;

    border: none;

    background-color: #f7f7f7;
    margin: 0;
    padding: 0;

    align-self: self-start;

    & > .field-renderer__item {
      padding: 0.5rem $padding $padding;
    }

    .field-dynamic > .elder-input__wrapper {
      & > .elder-input__field {
        border: none;
      }
    }
  }

  &--disabled {
    padding: 1rem;
  }

  &--expanded {
    & > .field-renderer__collapsible {
      &-header {
        background-color: transparent;
        border-bottom: 2px solid var(--vue-elder-border-color, #eaeaea);
      }
    }
  }

  &__disabled-overlay {
    $disabledColor: rgba(255, 255, 255, 0.805);

    position: absolute;
    z-index: 3;

    width: 100%;
    height: 100%;
    left: 0;
    top: 0;

    padding: 2rem 1rem;
    display: flex;
    justify-content: center;
    align-items: flex-start;
    flex-grow: 1;

    background-color: $disabledColor;
    border: 1px solid var(--vue-elder-border-color, #eaeaea);

    &-content {
      $size: 110px;

      position: sticky;
      top: 200px;

      background-color: var(--vue-elder-input-color, #f2f2f2);
      border: 1px solid var(--vue-elder-border-color, #eaeaea);
      border-radius: 50%;

      width: $size;
      height: $size;

      text-align: center;
      display: flex;
      flex-direction: column;
      align-items: center;
      justify-content: center;
      gap: 0.7rem;
    }

    &-info {
      font-size: 0.8rem;
    }
  }

  &__actions {
    display: flex;
    justify-content: end;
    gap: 0.5rem;
    margin-left: auto;

    &:hover {
      & + .field-dynamic > .elder-input__wrapper {
        & > .elder-input__field {
          &:before {
            position: absolute;
            content: '';
            height: 100%;
            width: 100%;
            border: 1.7px dashed var(--primary);
            border-radius: inherit;
            background-color: #eaeaea;
          }
        }
      }
    }
  }

  &__action {
    &-up,
    &-down,
    &-remove {
      font-size: 0.7rem !important;
    }
    &-settings {
      font-size: 0.9rem !important;
    }
  }

  &__collapsible {
    &-labels {
      padding: 0;
      display: flex;
      gap: 1rem;
      align-items: end;
      line-height: normal;

      &-label {
        line-height: normal;
        font-weight: bold;
      }

      &-sublabel {
        font-size: 0.75em;
        opacity: 0.5;
      }
    }

    &-header {
      display: flex;
      align-items: center;
      align-self: flex-start;
      gap: 1rem;
      grid-column: 1/-1;
      cursor: pointer;
      text-align: left;
      padding: $padding;
      border-top: inherit;

      background-color: hsl(var(--secondary-h), var(--secondary-s), 92%);
      &:focus-visible {
        outline: 2px solid currentColor !important;
      }

      .customfields-settings__trigger {
        border: 0;
        --vue-elder-button-padding-y: 0rem;
        --vue-elder-button-padding-x: 0.75rem;

        &:hover {
          opacity: 0.4;
          &:before {
            opacity: 0 !important;
          }
        }
        &:focus:before {
          opacity: 0 !important;
        }
      }
    }
  }

  &__item {
    flex-grow: 1;
    display: flex;
    flex-direction: column;
    gap: 0.5rem;
  }

  &__action-add {
    grid-column: 1/-1;

    background-color: rgba(black, 0.05) !important;
    border: none !important;
    justify-content: center !important;
    border-top-left-radius: 0 !important;
    border-top-right-radius: 0 !important;
    flex-direction: column-reverse;
    align-items: center;
    align-content: center;
    justify-items: center;

    .elder-button__label {
      flex-grow: 0;
      padding: 0.2rem 0 1rem;
    }

    .elder-button__icon {
      padding: 1rem 0 0;
      font-size: 1.6rem;
      color: var(--primary);
    }
  }

  .custom-field {
    flex-grow: 1;
  }
}
</style>
